import styles from "./EstimateLayoutStep3.module.scss";
import classNames from "classnames/bind";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import type { ISectionStep3FormData, EstimateLayoutStep3FormData as Step3FormData } from "~/utils/interface/estimateSetting";
import { useRef, useState } from "react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import BaseBoxContent from "~/components/common/base-box-content/BaseBoxContent";
import DroppableArea from "~/components/common/drop-drag/DroppableArea";
import { DragIcon } from "~/assets/icons";
import BaseUploadFileV2 from "~/components/common/base-upload-file-v2/BaseUploadFileV2";
import FormInput from "~/components/common/form/formInput/FormInput";
import AddItem from "~/components/common/add-item/AddItem";
import { PlusIcon, Trash2Icon } from "lucide-react";

const cx = classNames.bind(styles);

interface EstimateLayoutStep3Props {
    onStep3DataChange?: (data: Step3FormData) => void;
    initialData?: Step3FormData | null;
}

const defaultLine = () => ({ lineDescription: "", fileUrl: "", lineImage: undefined as File | undefined });

const EstimateLayoutStep3 = ({ onStep3DataChange, initialData }: EstimateLayoutStep3Props) => {
    const refs = useRef<any[]>([]);
    const DEFAULT_TITLE_STEP = "Inspection";
    const defaultSection = () => ({ sectionTitle: "Section", lines: [defaultLine()] });

    const [formData, setFormData] = useState<Step3FormData>(() => ({
        titleStep3: initialData?.titleStep3 ?? DEFAULT_TITLE_STEP,
        sections: initialData?.sections?.length
            ? initialData.sections
            : [defaultSection()],
    }));

    const handleUpdateTitle = (update: Partial<Step3FormData>) => {
        const data: Step3FormData = { ...formData, ...update };
        setFormData(data);
        onStep3DataChange?.(data);
    };

    const handleUpdateData = (sectionIndex: number, update: Partial<ISectionStep3FormData>) => {
        const sections = [...(formData.sections ?? [])];
        if (!sections[sectionIndex]) return;
        sections[sectionIndex] = { ...sections[sectionIndex], ...update };
        const data: Step3FormData = { ...formData, sections };
        setFormData(data);
        onStep3DataChange?.(data);
    };

    const handleUpdateLineData = (sectionIndex: number, lineIndex: number, update: Partial<NonNullable<ISectionStep3FormData["lines"]>[number]>) => {
        const sections = [...(formData.sections ?? [])];
        const section = sections[sectionIndex];
        if (!section?.lines) return;
        const lines = [...section.lines];
        if (!lines[lineIndex]) return;
        lines[lineIndex] = { ...lines[lineIndex], ...update };
        sections[sectionIndex] = { ...section, lines };
        const data: Step3FormData = { ...formData, sections };
        setFormData(data);
        onStep3DataChange?.(data);
    };

    const onAddSection = () => {
        const sections = [...(formData.sections ?? []), { sectionTitle: "Section", lines: [defaultLine()] }];
        const data: Step3FormData = { ...formData, sections };
        setFormData(data);
        onStep3DataChange?.(data);
    };

    const onRemoveSection = (index: number) => {
        const sections = [...(formData.sections ?? [])];
        sections.splice(index, 1);
        const data: Step3FormData = { ...formData, sections };
        setFormData(data);
        onStep3DataChange?.(data);
    };

    const onRemoveLine = (sectionIndex: number, lineIndex: number) => {
        const sections = [...(formData.sections ?? [])];
        const section = sections[sectionIndex];
        if (!section?.lines || section.lines.length <= 1) return;
        const lines = [...section.lines];
        lines.splice(lineIndex, 1);
        sections[sectionIndex] = { ...section, lines };
        const data: Step3FormData = { ...formData, sections };
        setFormData(data);
        onStep3DataChange?.(data);
    };

    const addLine = (sectionIndex: number) => {
        const sections = [...(formData.sections ?? [])];
        const section = sections[sectionIndex];
        if (!section) return;
        const lines = [...(section.lines ?? []), defaultLine()];
        sections[sectionIndex] = { ...section, lines };
        const data: Step3FormData = { ...formData, sections };
        setFormData(data);
        onStep3DataChange?.(data);
    };

    return (
        <div className={cx("estimate-layout-step-3-container")}>
            <div className={cx("header")}>
                <TitleEditor
                    value={formData.titleStep3}
                    name={""}
                    isViewMode={false}
                    placeholder={""}
                    inputkey={""}
                    onChange={(value) => handleUpdateTitle({ ...formData, titleStep3: value })}
                    styleView={{ fontSize: "20px", fontWeight: 700 }}
                />
                <div className={cx("button-container")}>
                    <PrimaryButton title="View Page" onClick={() => { }} className={cx("btn-preview")} />
                </div>
            </div>

            <div className={cx("content")}>
                {(formData.sections ?? []).map((section: ISectionStep3FormData, sectionIndex: number) => (
                    <div key={sectionIndex} className={cx("section-block")}>
                        <BaseBoxContent>
                            <div data-slot="header">
                                <div className={cx("box-header-inner")}>
                                    <div>
                                        <TitleEditor
                                            value={section.sectionTitle}
                                            name={""}
                                            placeholder={""}
                                            isViewMode={false}
                                            inputkey={""}
                                            onChange={(value) => handleUpdateData(sectionIndex, { sectionTitle: value })}
                                            styleView={{ fontSize: "18px" }}
                                        />
                                        <div className={cx("title-style")}>
                                            <p className={cx("style-label")}>Style: Standard</p>
                                            <p className={cx("style-change")}>Change</p>
                                        </div>
                                    </div>
                                    <button className={cx("button-remove-section")} onClick={() => onRemoveSection(sectionIndex)}>
                                        Remove
                                    </button>
                                </div>
                            </div>

                            <div data-slot="content">
                                <div className={cx("content-inner")}>
                                    <DroppableArea
                                        data={section.lines ?? []}
                                        onChange={(newArray: NonNullable<ISectionStep3FormData["lines"]>) => {
                                            const sections = [...(formData.sections ?? [])];
                                            if (sections[sectionIndex]) {
                                                sections[sectionIndex] = { ...sections[sectionIndex], lines: newArray };
                                            }
                                            const data: Step3FormData = { ...formData, sections };
                                            setFormData(data);
                                            onStep3DataChange?.(data);
                                        }}
                                        groupName="sections"
                                        renderComponent={(line, lineIndex) => (
                                            <>
                                                <div className={cx("line-row")}>
                                                    <div className={cx("drag-button")}>
                                                        <DragIcon className={cx("drag-icon")} />
                                                    </div>
                                                    <div className={cx("upload-wrap")}>
                                                        <BaseUploadFileV2
                                                            ref={(el) => (refs.current[sectionIndex] = el)}
                                                            url={line.fileUrl}
                                                            isViewMode={false}
                                                            onChange={(data: { file?: File; fileUrl?: string }) => {
                                                                handleUpdateLineData(sectionIndex, lineIndex, {
                                                                    lineImage: data.file,
                                                                    fileUrl: data.fileUrl,
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className={cx("input-wrap")}>
                                                        <FormInput
                                                            name="inspectionDescription"
                                                            text=""
                                                            isViewMode={false}
                                                            placeholder="Descriptions...."
                                                            inputkey="description"
                                                            value={line.lineDescription ?? ""}
                                                            inputType="textarea"
                                                            height="171px"
                                                            onChange={(e) =>
                                                                handleUpdateLineData(sectionIndex, lineIndex, {
                                                                    lineDescription: e.target.value,
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                    <div className={cx("line-actions")}>
                                                        {(section.lines?.length ?? 0) > 1 && (
                                                            <Trash2Icon
                                                                color="#FA6363"
                                                                onClick={() => onRemoveLine(sectionIndex, lineIndex)}
                                                                className={cx("icon-remove")}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    />
                                </div>
                            </div>

                            <div data-slot="footer">
                                <div className={cx("footer-inner")}>
                                    <AddItem onClick={() => addLine(sectionIndex)} label="Add Photos" />
                                </div>
                            </div>
                        </BaseBoxContent>
                    </div>
                ))}
            </div>

            <button className={cx("add-section")} onClick={onAddSection}>
                <PlusIcon />
                Add Section
            </button>
        </div>
    );
};

export default EstimateLayoutStep3;
